// Lista de uma aba (Recebidos ou Enviados) da tela `/app/splits` (WEB-123): filtros de
// status e período, os quatro estados (carregando, erro, vazio, filtro sem resultado) e a
// paginação por cursor. Mesma moldura da lista de entregas de Webhooks (WEB-121).

import type { ReactNode } from 'react';
import type { Split, SplitRole, SplitStatus } from '../../api/queries/splits.ts';
import { Button } from '../../components/ui/button.tsx';
import { SectionHead } from '../developers/section-head.tsx';
import { isSplitStatus, SPLIT_STATUS, SPLIT_STATUSES } from './model.ts';
import { SplitRow } from './split-row.tsx';

export const PERIODS = ['7d', '30d', '90d'] as const;
export type Period = (typeof PERIODS)[number];

const PERIOD_LABEL: Record<Period | 'all', string> = {
  all: 'Todo o período',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  '90d': 'Últimos 90 dias',
};

export function isPeriod(value: string): value is Period {
  return PERIODS.some((period) => period === value);
}

const COPY: Record<
  SplitRole,
  { title: string; hint: string; empty: string; emptyHint: string; listLabel: string }
> = {
  received: {
    title: 'Recebidos',
    hint: 'Parte das vendas de outros sellers que cai no seu saldo quando o PIX é pago.',
    empty: 'Nenhum split recebido ainda',
    emptyHint:
      'Para receber, autorize o e-mail da conta LeztyPay de quem vai dividir as vendas com você.',
    listLabel: 'Splits recebidos',
  },
  paid: {
    title: 'Enviados',
    hint: 'Parte das suas cobranças que vai para outros sellers.',
    empty: 'Nenhum split enviado ainda',
    emptyHint:
      'Você divide uma venda na Nova cobrança, com quem já autorizou o seu e-mail na conta dela.',
    listLabel: 'Splits enviados',
  },
};

const SELECT_CLASS =
  'h-11 min-w-0 rounded-xl border border-transparent bg-secondary/60 px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--piao-gold)]';

export interface SplitsListFilters {
  status: SplitStatus | undefined;
  period: Period | undefined;
}

export function SplitsList({
  side,
  rows,
  isLoading,
  error,
  onRetry,
  filters,
  onFiltersChange,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onSelect,
  selectedId,
  emptyAction,
  transactionLink,
}: {
  side: SplitRole;
  rows: Split[];
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
  filters: SplitsListFilters;
  onFiltersChange: (next: SplitsListFilters) => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onSelect: (split: Split) => void;
  selectedId?: string | undefined;
  /** Ação do estado vazio (sem filtro): o caminho para começar a receber ou enviar. */
  emptyAction?: ReactNode;
  /** Link para a transação, só na aba Enviados. */
  transactionLink?: (transactionId: string) => ReactNode;
}) {
  const copy = COPY[side];
  const filtered = filters.status !== undefined || filters.period !== undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionHead title={copy.title} hint={copy.hint} />

      <div className="flex flex-wrap gap-2">
        <select
          aria-label="Status"
          value={filters.status ?? 'all'}
          onChange={(event) => {
            const value = event.target.value;
            onFiltersChange({ ...filters, status: isSplitStatus(value) ? value : undefined });
          }}
          className={SELECT_CLASS}
        >
          <option value="all">Todos os status</option>
          {SPLIT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {SPLIT_STATUS[status].label}
            </option>
          ))}
        </select>
        <select
          aria-label="Período"
          value={filters.period ?? 'all'}
          onChange={(event) => {
            const value = event.target.value;
            onFiltersChange({ ...filters, period: isPeriod(value) ? value : undefined });
          }}
          className={SELECT_CLASS}
        >
          <option value="all">{PERIOD_LABEL.all}</option>
          {PERIODS.map((period) => (
            <option key={period} value={period}>
              {PERIOD_LABEL[period]}
            </option>
          ))}
        </select>
      </div>

      <div
        style={{
          padding: '4px 12px',
          borderRadius: 18,
          background: 'var(--piao-surface)',
          border: '1px solid var(--piao-surface-border)',
        }}
      >
        {error !== null && error !== undefined && rows.length === 0 ? (
          <div
            role="alert"
            style={{ padding: '18px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <p style={{ margin: 0, fontSize: 13, color: 'var(--piao-danger)' }}>
              Não deu para carregar os splits.
            </p>
            <Button variant="secondary" size="sm" className="self-start" onClick={onRetry}>
              Tentar de novo
            </Button>
          </div>
        ) : isLoading ? (
          <div
            role="status"
            aria-busy="true"
            aria-label="Carregando splits"
            style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 0' }}
          >
            {[0, 1, 2].map((key) => (
              <div
                key={key}
                style={{ height: 56, borderRadius: 12, background: 'var(--piao-wash)' }}
              />
            ))}
          </div>
        ) : rows.length === 0 ? (
          filtered ? (
            <div style={{ padding: '18px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--piao-muted)' }}>
                Nenhum split com esses filtros.
              </p>
              <button
                type="button"
                className="piao-link focus-visible:ring-2 focus-visible:ring-[color:var(--piao-gold)] focus-visible:outline-none"
                style={{
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 44,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  onFiltersChange({ status: undefined, period: undefined });
                }}
              >
                Limpar filtros
              </button>
            </div>
          ) : (
            <div style={{ padding: '36px 16px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontWeight: 700, color: 'var(--piao-ink)' }}>{copy.empty}</p>
              <p
                style={{
                  margin: '6px auto 16px',
                  maxWidth: 440,
                  fontSize: 13,
                  lineHeight: 1.5,
                  color: 'var(--piao-muted)',
                }}
              >
                {copy.emptyHint}
              </p>
              {emptyAction}
            </div>
          )
        ) : (
          <ul aria-label={copy.listLabel} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {rows.map((split, index) => (
              <li key={split.id}>
                <SplitRow
                  split={split}
                  side={side}
                  divider={index > 0}
                  selected={split.id === selectedId}
                  onSelect={() => {
                    onSelect(split);
                  }}
                  {...(transactionLink === undefined
                    ? {}
                    : { transactionLink: transactionLink(split.transaction.id) })}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="secondary" size="sm" disabled={isLoadingMore} onClick={onLoadMore}>
            {isLoadingMore ? 'Carregando…' : 'Carregar mais'}
          </Button>
        </div>
      )}
    </div>
  );
}
